import React, { useState } from "react";

const TABS = [
  { key: "about", label: "About" },
  { key: "objective", label: "Objective" },
  { key: "scoring", label: "Scoring" },
  { key: "howto", label: "How to Play" },
  { key: "rewards", label: "Rewards" },
];

export default function DroneDashHelp({ onClose }) {
  const [tab, setTab] = useState("about");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-3xl rounded-3xl bg-slate-950 border border-slate-800 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h2 className="text-cyan-200 font-bold text-lg">🚁 DroneDash Help</h2>
          <button onClick={onClose} className="text-xs text-slate-300 border border-slate-700 rounded-lg w-fit px-3 py-1">
            Close
          </button>
        </div>

        <div className="flex gap-1 px-3 pt-3 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${
                tab === t.key ? "bg-sky-500 text-white" : "bg-slate-800 text-slate-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 overflow-y-auto text-sm text-slate-300 space-y-3">
          {tab === "about" && (
            <>
              <p className="text-gray-100">
                DroneDash is a quick, one-thumb flying game — steer a delivery drone across
                three lanes, dodge hazards, and grab pickups. Most runs last 30–90 seconds,
                so it's built for a couple of minutes a day, not a long session.
              </p>
              <p className="text-gray-100">
                Your drone never fires anything — it only flies, dodges, and delivers.
                Conflict mode just adds tougher hazards (missiles, satellites, drone swarms)
                for players who want a harder challenge; the drone itself stays a
                relief/delivery flyer, not a weapon.
              </p>
            </>
          )}

          {tab === "objective" && (
            <>
              <p className="text-gray-100"><strong className="text-white">Survive and deliver.</strong> Two ways to end a run:</p>
              <ul className="list-disc list-inside space-y-1">
                <li className="text-gray-100">Your battery hits 0% (drains constantly — refuel with 🔋 pickups)</li>
                <li className="text-gray-100">You crash into a hazard with no shield active</li>
              </ul>
              <p className="text-gray-100">Fly across all 3 lanes to collect pickups — camping in one lane too long drains your battery faster (see Restless below).</p>
            </>
          )}

          {tab === "scoring" && (
            <>
              <p className="text-gray-100"><strong className="text-white">Score = distance flown + pickups + bonuses.</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li className="text-gray-100">🍬 Toffee, 🍎 Food — small-to-medium points</li>
                <li className="text-gray-100">🧺 Agri produce — medium-high points</li>
                <li className="text-gray-100">🧶 Women-made craft — highest pickup value, rare</li>
                <li className="text-gray-100">💡 Fact orb — points + a fun drone fact</li>
                <li className="text-gray-100">🛡 Shield — absorbs your next hit for free</li>
                <li className="text-gray-100"><strong className="text-white">Close call!</strong> bonus for passing a hazard in the lane right next to you without hitting it</li>
                <li className="text-gray-100"><strong className="text-white">Restless</strong> penalty if you stay in one lane too long — keep moving!</li>
              </ul>
              <p className="text-gray-100">Combo grows with consecutive good pickups/near-misses and resets on a hit — chain pickups for bigger bonuses.</p>
            </>
          )}

          {tab === "howto" && (
            <>
              <p className="text-gray-100"><strong className="text-white">Controls:</strong> tap left/right on screen, use the on-screen lane buttons, or arrow keys / A-D on a keyboard.</p>
              <p className="text-gray-100"><strong className="text-white">Difficulty:</strong> Easy is built for younger players — slower drain, gentler pace. Medium and Hard ramp up speed, hazards, and unlock Conflict mode.</p>
              <p className="text-gray-100"><strong className="text-white">Mode:</strong> Peace keeps hazards simple (birds, power lines). Conflict adds missiles (with a warning flash before they arrive), drone swarms, and satellites — only on Medium/Hard.</p>
            </>
          )}

          {tab === "rewards" && (
            <>
              <p className="text-gray-100"><strong className="text-white">Streak:</strong> play once a day to keep your streak alive — tracked on our server, not just your device, so it's safe even if you switch phones.</p>
              <p className="text-gray-100"><strong className="text-white">Leaderboard:</strong> top 10 players, your single best run only — no clutter from repeat entries.</p>
              <p className="text-gray-100"><strong className="text-white">Gumball:</strong> set a streak or points goal with your parent/mentor. Hit it, and they log a reward for you — check "Rewards" from the home screen to see your reward history anytime.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
